import { useMemo, type JSX } from 'react';

import { PriceSimulationHouseHoldSizeDto } from '@eneco/dc-be/client';
import mapScRadioToMap from '@eneco/dc-be/utils/mapScRadioToMap';
import { Button, Heading, RadioGroup, RadioTile, Stack, Stretch, Text } from '@eneco/sparky';
import { FivePeople, FourPeople, OnePerson, ThreePeople, TwoPeople } from '@eneco/sparky/illustrations';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { Controller, useFormContext } from 'react-hook-form';

import { NUMBER_OF_PEOPLE_IN_PROPERTY_OPTIONS, PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

const getHouseholdSizeImage = (option: PriceSimulationHouseHoldSizeDto) => {
  switch (option) {
    case 'One':
      return <OnePerson size="small" />;
    case 'Two':
      return <TwoPeople size="small" />;
    case 'Three':
      return <ThreePeople size="small" />;
    case 'Four':
      return <FourPeople size="small" />;
    case 'FiveOrMore':
      return <FivePeople size="small" />;
  }
};

export const HouseholdSizeStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const {
    control,
    trigger,
    formState: { errors },
    getValues,
  } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();

  const householdSizeOptions = NUMBER_OF_PEOPLE_IN_PROPERTY_OPTIONS.map(option => ({
    name: option,
    label: option,
  }));

  const sitecoreHouseholdSizeOptions = mapScRadioToMap(
    fields.numberOfPeopleInPropertyStep.numberOfPeopleInPropertyList,
  );

  const heatingOptionsMap = useMemo(() => {
    const map = new Map<PriceSimulationHouseHoldSizeDto, { label: string; name: string; image?: JSX.Element }>();
    for (const option of householdSizeOptions) {
      map.set(option.name, {
        label: sitecoreHouseholdSizeOptions.get(option.name) ?? option.name + ' [not in Sitecore]',
        name: option.name,
        image: getHouseholdSizeImage(option.name),
      });
    }
    return map;
  }, [sitecoreHouseholdSizeOptions, householdSizeOptions]);

  const onNextClick = async () => {
    const isValid = await trigger('houseHoldSize');
    if (isValid) {
      trackEventBE('pbt_calculation_unknown - family', {
        status: 'click',
        data: getTrackableFormValues(getValues()),
      });
      nextMainStep();
    }
  };

  return (
    <>
      <Stack gap="2">
        <Heading as="h1" size="M">
          {fields.numberOfPeopleInPropertyStep.title.value}
        </Heading>
        {fields.numberOfPeopleInPropertyStep.subTitle.value && (
          <Text>{fields.numberOfPeopleInPropertyStep.subTitle.value}</Text>
        )}
      </Stack>
      <Stretch>
        <div>
          <Controller
            control={control}
            name={'houseHoldSize'}
            render={({ field: { onChange, value } }) => {
              return (
                <RadioGroup
                  label={''}
                  name={'houseHoldSize'}
                  isFullWidth
                  value={value}
                  onValueChange={onChange}
                  error={
                    errors.houseHoldSize?.message
                      ? fields.numberOfPeopleInPropertyStep.numberOfPeopleInPropertyList.value.requiredMessage
                      : undefined
                  }>
                  {Array.from(heatingOptionsMap.values()).map(({ name, label, image }) => (
                    <RadioTile key={name} icon={image} value={name}>
                      {label}
                    </RadioTile>
                  ))}
                </RadioGroup>
              );
            }}
          />
        </div>
      </Stretch>
      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
    </>
  );
};
